import { useState } from 'react';
import {
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  ChevronRight,
  Loader2,
  Ban,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useApprenantTimeline, type Stage } from '../hooks/useApprenantTimeline.js';
import { useApprenantMutation } from '../hooks/useApprenant.js';

interface Props {
  apprenantId: string;
}

const STAGE_ORDER: Stage[] = ['Inscrit', 'Début', 'Milieu', 'Fin', 'Terminé'];
const HORS_FLOW: Stage[] = ['Alerte', 'Abandon'];

/**
 * Slice TimelineCard — domaine Référentiel (Apprenants)
 *
 * Pleine largeur en haut de la Fiche Apprenant : donne en un coup d'œil
 * où on en est dans le parcours pédagogique de cet élève + la prochaine
 * action à mener.
 *
 * Phase wiring 4 (28/05/2026) :
 *  - Pipeline éditable : clic sur une étape = PATCH statutZoho (optimistic)
 *  - 2 chips hors-flow à droite : Alerte / Abandon (workflow exceptionnel)
 *  - Note : la stage sera ré-écrite automatiquement par le dépôt du
 *    contrat signé (onglet Contractualisation) une fois cette feature
 *    livrée. En attendant, l'override manuel ici fait foi.
 */
export function TimelineCard({ apprenantId }: Props) {
  const { data: t, isLoading, error } = useApprenantTimeline(apprenantId);
  const patch = useApprenantMutation(apprenantId);
  const qc = useQueryClient();
  const [pendingStage, setPendingStage] = useState<Stage | null>(null);
  const [stageError, setStageError] = useState<string | null>(null);

  const handleStageChange = async (next: Stage) => {
    if (!t || next === t.currentStage) return;
    setStageError(null);
    setPendingStage(next);
    try {
      await patch({ statutZoho: next });
      // Invalide timeline + identité (statut affiché dans IdentiteCard)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['v2', 'apprenant-timeline', apprenantId] }),
        qc.invalidateQueries({ queryKey: ['v2', 'apprenant', apprenantId] }),
      ]);
    } catch (e) {
      setStageError((e as Error).message);
    } finally {
      setPendingStage(null);
    }
  };

  if (isLoading) return <div className="h-32 animate-pulse rounded-xl bg-neutral-100" />;
  if (error || !t)
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        Timeline indisponible : {error?.message ?? 'aucune donnée'}
      </div>
    );

  const currentIdx = STAGE_ORDER.indexOf(t.currentStage);
  const isHorsFlow = HORS_FLOW.includes(t.currentStage);

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
      {/* Header */}
      <div className="mb-5 flex items-baseline justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
          <Clock className="text-primary-500 h-4 w-4" />
          Parcours pédagogique
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-neutral-500">
            Étape actuelle :{' '}
            <span
              className={`font-semibold ${
                isHorsFlow
                  ? t.currentStage === 'Abandon'
                    ? 'text-rose-700'
                    : 'text-amber-700'
                  : 'text-neutral-900'
              }`}
            >
              {t.currentStage}
            </span>{' '}
            · depuis{' '}
            <span className="font-semibold text-neutral-900">{t.daysOnCurrentStage} jours</span>
          </div>
          {/* Chips hors-flow */}
          <div className="flex items-center gap-1">
            {HORS_FLOW.map((stage) => {
              const isActive = t.currentStage === stage;
              const isPending = pendingStage === stage;
              const Icon = stage === 'Alerte' ? AlertTriangle : Ban;
              const colorBase =
                stage === 'Alerte'
                  ? isActive
                    ? 'border-amber-400 bg-amber-100 text-amber-800'
                    : 'border-amber-200 bg-white text-amber-600 hover:bg-amber-50'
                  : isActive
                    ? 'border-rose-400 bg-rose-100 text-rose-800'
                    : 'border-rose-200 bg-white text-rose-600 hover:bg-rose-50';
              return (
                <button
                  key={stage}
                  type="button"
                  onClick={() => handleStageChange(stage)}
                  disabled={isPending || isActive}
                  title={
                    isActive ? `Stage actuelle : ${stage}` : `Basculer en ${stage} (hors-flow)`
                  }
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors disabled:opacity-60 ${colorBase}`}
                >
                  {isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Icon className="h-3 w-3" />
                  )}
                  {stage}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pipeline horizontal — chaque étape est cliquable */}
      <div className="relative mb-3">
        {/* Ligne de fond */}
        <div className="absolute left-4 right-4 top-3 h-0.5 bg-neutral-200" />
        {/* Ligne progression (jusqu'à l'étape actuelle) */}
        {currentIdx >= 0 && (
          <div
            className="absolute left-4 top-3 h-0.5 bg-emerald-500 transition-all"
            style={{
              width: `calc(${(currentIdx / (STAGE_ORDER.length - 1)) * 100}% - ${currentIdx === 0 ? 0 : 32}px)`,
            }}
          />
        )}
        {/* Étapes */}
        <div className="relative flex justify-between">
          {STAGE_ORDER.map((stage, idx) => {
            const transition = t.history.find((h) => h.stage === stage);
            const isPast = idx < currentIdx;
            const isCurrent = idx === currentIdx;
            const isPending = pendingStage === stage;
            return (
              <button
                key={stage}
                type="button"
                onClick={() => handleStageChange(stage)}
                disabled={isPending || isCurrent}
                title={
                  isCurrent
                    ? `Étape actuelle (depuis ${t.daysOnCurrentStage} j)`
                    : `Cliquer pour basculer en "${stage}"`
                }
                className="group flex flex-col items-center disabled:cursor-default"
                style={{ width: '20%' }}
              >
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full border-2 transition-all ${
                    isPending
                      ? 'border-primary-500 bg-primary-100 text-primary-700 animate-pulse'
                      : isPast
                        ? 'border-emerald-500 bg-emerald-500 text-white group-hover:scale-110 group-hover:ring-2 group-hover:ring-emerald-200'
                        : isCurrent
                          ? 'border-primary-500 bg-primary-50 text-primary-700 ring-primary-200 ring-2'
                          : 'group-hover:border-primary-400 group-hover:bg-primary-50 border-neutral-300 bg-white text-neutral-400 group-hover:scale-110'
                  }`}
                >
                  {isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : isPast ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : isCurrent ? (
                    <div className="bg-primary-500 h-2 w-2 rounded-full" />
                  ) : (
                    <Circle className="h-3 w-3" />
                  )}
                </div>
                <div
                  className={`mt-1.5 text-xs font-semibold transition-colors ${
                    isCurrent
                      ? 'text-primary-700'
                      : isPast
                        ? 'text-neutral-700'
                        : 'group-hover:text-primary-600 text-neutral-400'
                  }`}
                >
                  {stage}
                </div>
                {transition?.enteredAt && (
                  <div className="mt-0.5 text-[10px] text-neutral-400">
                    {new Date(transition.enteredAt).toLocaleDateString('fr-FR')}
                  </div>
                )}
                {isCurrent && (
                  <div className="text-primary-600 mt-0.5 text-[10px] font-medium">
                    {t.daysOnCurrentStage}j
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Aide manipulation + erreur PATCH */}
      <div className="mb-3 flex items-center justify-between gap-3 text-[10px] text-neutral-500">
        <div>
          💡 Clic sur une étape = basculer le statut · sera réécrit auto au dépôt du contrat signé.
        </div>
        {stageError && <div className="text-rose-700">⚠ Échec du PATCH : {stageError}</div>}
      </div>

      {/* Prochaine action */}
      {t.nextAction && (
        <div
          className={`flex items-center justify-between rounded-lg border p-3 ${
            t.nextAction.overdueDays && t.nextAction.overdueDays > 0
              ? 'border-rose-200 bg-rose-50'
              : 'border-amber-200 bg-amber-50'
          }`}
        >
          <div className="flex items-center gap-3">
            {t.nextAction.overdueDays && t.nextAction.overdueDays > 0 ? (
              <AlertTriangle className="h-5 w-5 text-rose-600" />
            ) : (
              <Clock className="h-5 w-5 text-amber-600" />
            )}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-600">
                Prochaine action requise
              </div>
              <div className="mt-0.5 text-sm font-medium text-neutral-900">
                {t.nextAction.label}
              </div>
              {t.nextAction.dueDate && (
                <div className="mt-0.5 text-xs text-neutral-500">
                  Prévue le{' '}
                  <span className="font-medium">
                    {new Date(t.nextAction.dueDate).toLocaleDateString('fr-FR')}
                  </span>
                  {t.nextAction.overdueDays !== null && t.nextAction.overdueDays > 0 && (
                    <span className="ml-2 font-semibold text-rose-700">
                      ⚠ Retard de {t.nextAction.overdueDays} jours
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <button className="text-primary-700 hover:bg-primary-50 flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold shadow-sm">
            Exécuter
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
